export type PurchasePlan = "sync" | "unknown";

export type PurchasePackage = {
  identifier: string;
  title: string;
  priceText: string;
  plan: PurchasePlan;
  billingPeriod: "monthly" | "annual" | "other";
  /**
   * Length of the free trial this customer is eligible for on this package,
   * in days, or null when there is none (RevenueCat only returns trial offers
   * to customers who qualify, so a returning subscriber gets null).
   */
  trialDays: number | null;
  raw: unknown;
};

export type PurchaseCustomerInfo = {
  activeEntitlements: string[];
  hasSync: boolean;
  /**
   * Whether the active subscription will renew (and bill) again. False for a
   * cancelled-but-not-yet-expired subscription, and for accounts with access
   * that isn't a paid subscription (comped access).
   */
  willRenew: boolean;
  managementUrl: string | null;
  originalAppUserId: string | null;
};

export type PaywallPresentationResult =
  "purchased" | "restored" | "cancelled" | "not_presented" | "error";

export type PurchaseState = {
  configured: boolean;
  loading: boolean;
  purchasing: boolean;
  managing: boolean;
  packages: PurchasePackage[];
  customerInfo: PurchaseCustomerInfo | null;
  hasSync: boolean;
  error: string | null;
};

export type CustomerInfoListener = (info: PurchaseCustomerInfo) => void;

export type PurchaseService = {
  load(userId: string): Promise<{
    configured: boolean;
    packages: PurchasePackage[];
    customerInfo: PurchaseCustomerInfo;
  }>;
  purchase(pkg: PurchasePackage): Promise<PurchaseCustomerInfo>;
  restore(): Promise<PurchaseCustomerInfo>;
  getCustomerInfo(): Promise<PurchaseCustomerInfo>;
  getManagementUrl(): Promise<string | null>;
  presentPaywall(): Promise<PaywallPresentationResult>;
  presentPaywallIfNeeded(): Promise<PaywallPresentationResult>;
  presentCustomerCenter(): Promise<void>;
  addCustomerInfoListener(listener: CustomerInfoListener): () => void;
  supportsNativePaywall: boolean;
  supportsCustomerCenter: boolean;
};

export function inferPurchasePlan(...values: (string | null | undefined)[]) {
  const haystack = values.filter(Boolean).join(" ").toLowerCase();
  if (
    haystack.includes("durance") ||
    haystack.includes("sync") ||
    haystack.includes("monthly") ||
    haystack.includes("$rc_monthly")
  ) {
    return "sync";
  }
  return "unknown";
}

/**
 * Converts a store period ("2 weeks", "14 days", "DAY"/"WEEK" in the native
 * SDK's uppercase form) into whole days. Months and years are approximated
 * (30/365) — trials are short, this is only used for the button label.
 */
export function trialDaysFromPeriod(
  period: { number: number; unit: string } | null | undefined,
): number | null {
  if (!period || !(period.number > 0)) return null;
  switch (period.unit.toLowerCase()) {
    case "day":
      return period.number;
    case "week":
      return period.number * 7;
    case "month":
      return period.number * 30;
    case "year":
      return period.number * 365;
    default:
      return null;
  }
}

function billingSuffix(billingPeriod: PurchasePackage["billingPeriod"]) {
  return billingPeriod === "annual" ? "/yr" : "/mo";
}

/** Button label for a package, leading with the trial when one applies. */
export function subscribeLabel(pkg: PurchasePackage) {
  if (pkg.trialDays) return `Start ${pkg.trialDays}-day free trial`;
  if (pkg.billingPeriod === "other") return `${pkg.title} — ${pkg.priceText}`;
  return `Subscribe — ${pkg.priceText}${billingSuffix(pkg.billingPeriod)}`;
}

/** One-line price disclosure shown with the button: what happens after the trial. */
export function pricingSummary(pkg: PurchasePackage) {
  const price = `${pkg.priceText}${billingSuffix(pkg.billingPeriod)}`;
  return pkg.trialDays
    ? `${pkg.trialDays}-day free trial, then ${price}. Cancel any time.`
    : `${price}. Cancel any time.`;
}

export function purchasePlanRank(plan: PurchasePlan) {
  return plan === "sync" ? 0 : 1;
}

export function purchaseBillingRank(
  billingPeriod: PurchasePackage["billingPeriod"],
) {
  if (billingPeriod === "monthly") return 0;
  if (billingPeriod === "annual") return 1;
  return 2;
}

export function purchaseErrorMessage(error: unknown, fallback: string) {
  if (
    error &&
    typeof error === "object" &&
    "userCancelled" in error &&
    (error as { userCancelled?: boolean }).userCancelled
  ) {
    return null;
  }
  if (error instanceof Error && error.message) return error.message;
  return fallback;
}
